import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

// Compact "answers left today" hint shown near the chat composer for signed-in
// users. Reads the server-side counter (GET /ai/usage) and refreshes whenever an
// answer completes (window event 'evermin:ai-used'). Hidden for guests.
export function UsageNudge({ className = "" }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [u, setU] = useState(null);

  const load = useCallback(() => {
    if (!user) { setU(null); return; }
    api.get("/ai/usage").then(({ data }) => setU(data)).catch(() => setU(null));
  }, [user]);

  useEffect(() => {
    load();
    const onUsed = () => load();
    window.addEventListener("evermin:ai-used", onUsed);
    return () => window.removeEventListener("evermin:ai-used", onUsed);
  }, [load]);

  if (!user || !u) return null;

  const exhausted = u.remaining <= 0;
  const low = u.remaining <= Math.max(2, Math.ceil(u.limit * 0.2));
  const tone = exhausted ? "text-rose-600" : low ? "text-amber-600" : "text-slate-400";
  const canUpgrade = u.tier !== "pro" && !!u.next_tier;

  return (
    <div className={`flex items-center gap-1.5 text-xs ${tone} ${className}`} data-testid="usage-nudge">
      <Gauge className="h-3.5 w-3.5" />
      <span data-testid="usage-nudge-text">
        {exhausted ? t("usage_exhausted_short") : t("usage_nudge").replace("{n}", u.remaining)}
      </span>
      {(low || exhausted) && canUpgrade && (
        <button onClick={() => navigate("/membership")} data-testid="usage-nudge-upgrade"
          className="font-semibold text-primary hover:underline">
          {t("usage_upgrade")}
        </button>
      )}
    </div>
  );
}
